/**
 * Document Utility Functions
 * Helper functions for document icons, labels, and formatting
 */

import React from 'react';
import { FileIcon } from '../assets/FileIcon';
import { ImageIcon } from '../assets/ImageIcon';
import { ExcelIcon } from '../assets/ExcelIcon';
import { MsWordIcon } from '../assets/MsWordIcon';

/**
 * Get document icon component based on content type
 */
export const getDocumentIcon = (contentType: string, extension: string): React.ReactNode => {
  // PDF - Use FileIcon component
  if (contentType === 'application/pdf' || extension === '.pdf') {
    return <FileIcon />;
  }

  // Word - Use MsWordIcon component
  if (
    contentType === 'application/msword' ||
    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    extension === '.doc' ||
    extension === '.docx'
  ) {
    return <MsWordIcon />;
  }

  // Excel and CSV - Use ExcelIcon component
  if (
    contentType === 'application/vnd.ms-excel' ||
    contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    contentType === 'text/csv' ||
    extension === '.xls' ||
    extension === '.xlsx' ||
    extension === '.csv'
  ) {
    return <ExcelIcon />;
  }

  // Images - Use ImageIcon component
  if (
    contentType.startsWith('image/') ||
    extension === '.png' ||
    extension === '.jpg' ||
    extension === '.jpeg'
  ) {
    return <ImageIcon />;
  }

  // Default
  return '📁';
};

/**
 * Get document icon color based on content type
 * Matches mobile implementation color scheme
 */
export const getDocumentIconColor = (contentType: string, extension: string): string => {
  // PDF - Red
  if (contentType === 'application/pdf' || extension === '.pdf') {
    return '#E53935';
  }

  // Word - Blue
  if (
    contentType === 'application/msword' ||
    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    extension === '.doc' ||
    extension === '.docx'
  ) {
    return '#2196F3';
  }

  // Excel and CSV - Green
  if (
    contentType === 'application/vnd.ms-excel' ||
    contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    contentType === 'text/csv' ||
    extension === '.xls' ||
    extension === '.xlsx' ||
    extension === '.csv'
  ) {
    return '#4CAF50';
  }

  // Images - Orange
  if (
    contentType.startsWith('image/') ||
    extension === '.png' ||
    extension === '.jpg' ||
    extension === '.jpeg'
  ) {
    return '#FF9800';
  }

  // Default - Gray
  return '#757575';
};

/**
 * Get human-readable type label for document
 */
export const getDocumentTypeLabel = (contentType: string, extension: string): string => {
  if (contentType === 'application/pdf' || extension === '.pdf') {
    return 'PDF';
  }

  if (
    contentType === 'application/msword' ||
    extension === '.doc'
  ) {
    return 'Word';
  }

  if (
    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    extension === '.docx'
  ) {
    return 'Word';
  }

  if (
    contentType === 'application/vnd.ms-excel' ||
    extension === '.xls'
  ) {
    return 'Excel';
  }

  if (
    contentType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    extension === '.xlsx'
  ) {
    return 'Excel';
  }

  if (
    contentType === 'text/csv' ||
    extension === '.csv'
  ) {
    return 'Excel';  // Treat CSV as Excel per requirements
  }

  if (contentType.startsWith('image/') || ['.png', '.jpg', '.jpeg'].includes(extension)) {
    return 'Image';
  }

  return 'File';
};

/**
 * Format file size to human-readable string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * Format document date for display
 */
export const formatDocumentDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch (error) {
    console.error('[DocumentUtils] Failed to format date', { dateString, error });
    return dateString;
  }
};

/**
 * Extract filename without extension from full filename
 */
export const getFileNameWithoutExtension = (filename: string): string => {
  return filename.replace(/\.[^.]+$/, '');
};
